import { useSelector } from "react-redux";
import { ActionItemsEndPointLogic } from "../logic/AssetManagement/ActionItems/actionItemsEndPointLogic";
import {
  selectUserToken,
  setDisplayErrorSnackBar,
} from "../features/userSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setSelectedAssignee,
  setSelectedCategory,
  setSelectedDueDate,
  setSelectedPriority,
  setSelectedStatus,
  setSelectedTarget,
} from "../features/actionItemsSlice";


export const useUpdateAcionItemsStatus = (action_name: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userToken = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedSearchSite } = useSelector(
    (state: any) => state?.actionItems
  );
  const [loading, setLoading] = useState(false);
  const reference_url = `${window.location.origin}${location.pathname}`;


// Function to get success message based on action(snooze/assign/resolved)
  const getSuccessMessage = (action : string) => {
    switch (action) {
      case "snoozed":
        return "Selected tasks have been snoozed.";
      case "assigned":
        return "Tasks have been assigned successfully.";
      case "resolved":
        return "Tasks have been resolved successfully.";
      default:
        return "Action completed successfully.";
    }
  };
  

  const updateActionItemsStatus = async (
    statusUpdateBody: any,
    comment: any,
    closeActionModal: any
  ) => {

    //-- Request body for updating task comment
    const commentBody = {
        object_type: "tasks",
        comment: comment,
        users: [],
        reference_module: "Action Items",
        reference_url: reference_url
    };
    try {
      setLoading(true);

      // Step 1: Update task status API call
      const res = await ActionItemsEndPointLogic.changeActionTaskStatus(
        userToken,
        statusUpdateBody
      );

      if (res?.status !== 200) {
        throw new Error(`Failed to update status. ${res?.reason}.`);
      }

      //  Extract updated tasks from the response(those tasks have updated are true)
      const updatedTasks =
        res?.data?.data.filter((update: any) => update.updated) || [];

      if (updatedTasks.length === 0) {
        throw new Error("No tasks were updated.");
      }

      // Call Comment API once per updated task(those task are true then update thier comment otherwise not)
      const commentPromises = updatedTasks.map(async (task: any) => {
        if (!comment)
          return { success: true, task_shared_uid: task.task_shared_uid };
        const commentRes =
          await ActionItemsEndPointLogic.commentActionItemsTask(
            userToken,
            commentBody,
            task.task_shared_uid
          );

        if (commentRes?.reason !== "Created.") {
          throw new Error(
            `Failed to add comment for task ${task.task_shared_uid}`
          );
        }

        return { success: true, task_shared_uid: task.task_shared_uid };
      });

      //-- Wait for all comment API calls to complete (Comment API)
      const results = await Promise.allSettled(commentPromises);

      //-- Check if all requests were successful
      const allSuccessful = results.every(
        (result) => result.status === "fulfilled"
      );

      if (allSuccessful) {
        dispatch(
            setDisplayErrorSnackBar({
              message: getSuccessMessage(action_name),
              type: "success",
            })
          );
        closeActionModal(); //Close modal if success
        //-- Fetch updated action items
        await ActionItemsEndPointLogic.getAllActionItems(
          selectedSearchSite,
          userToken,
          dispatch
        );
        navigate("/tools/action_items");
      } else {
        //-- Check for failed tasks
        const failedComments = results
          .filter((result: any) => result.status === "rejected")
          .map((result: any) => result.reason)
          .join(", ");
        dispatch(
          setDisplayErrorSnackBar({
            message: `Error in adding comments: ${failedComments}`,
            type: "error",
          })
        );
      }
    } catch (error) {
        // Ensure error message is a string
        const errorMessage =
          typeof error === "string"
            ? error
            : error instanceof Error
            ? error.message
            : "An unexpected error occurred.";
      
        dispatch(setDisplayErrorSnackBar({ message: errorMessage, type: "error" }));
      
        return; // Stop execution, do not close modal
    } finally {
      //-- Remove filters from URL & reset state
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("filters");
      dispatch(setSelectedCategory(null));
      dispatch(setSelectedStatus(null));
      dispatch(setSelectedDueDate(null));
      dispatch(setSelectedPriority(null));
      dispatch(setSelectedAssignee(null));
      dispatch(setSelectedTarget(null));
      setSearchParams(updatedParams, { replace: true });
      setLoading(false);
    }
  };

  return {
    loading,
    updateActionItemsStatus,
  };
};
